import { useTranslationUserProfiles } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	normalizePhoneNumberForBE,
	normalizePhoneNumberForFormValueComparison,
} from '@dealstack/shared/lib/format/string';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isSameDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import {
	getUserProfile,
	updateLegalRepresentativeWithoutMipId,
	updateVehicleDataWithoutMipId,
} from '@dealstack/frontend/shared/dataExchange/services/user.service';
import {
	UpdateUserRepresentativeDto,
	UpdateUserVehicleDataDto,
} from '@dealstack/shared/lib/generated';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

export enum FORM_TYPE {
	PIV = 'piv',
	LEGAL_REPRESENTATIVE = 'legalRepresentative',
}

export enum PIV_FORM {
	IS_PIV = 'isPIV',
	PIV_NAME = 'pivName',
	PIV_REG_NUMBER = 'pivRegNumber',
	PIV_COUNTRY = 'pivCountry',
	PIV_CITY = 'pivCity',
	PIV_POST_CODE = 'pivPostCode',
	PIV_STREET = 'pivStreet',
	PIV_HOUSE_NUMBER = 'pivHouseNumber',
}
export enum LEGAL_REPRESENTATIVE_FORM {
	IS_REPRESENTATIVE_SAME_AS_INVESTOR = 'isRepresentativeSameAsInvestor',
	REPRESENTATIVE_FIRST_NAME = 'representativeFirstName',
	REPRESENTATIVE_LAST_NAME = 'representativeLastName',
	REPRESENTATIVE_BIRTH_DATE = 'representativeBirthDate',
	REPRESENTATIVE_EMAIL = 'representativeEmail',
	REPRESENTATIVE_TELEPHONE_PREFIX = 'representativeTelephonePrefix',
	REPRESENTATIVE_PHONE_NUMBER = 'representativePhoneNumber',
	REPRESENTATIVE_COUNTRY = 'representativeCountry',
	REPRESENTATIVE_CITY = 'representativeCity',
	REPRESENTATIVE_POST_CODE = 'representativePostCode',
	REPRESENTATIVE_STREET = 'representativeStreet',
	REPRESENTATIVE_HOUSE_NUMBER = 'representativeHouseNumber',
}
export const useManagePIVController = () => {
	const tUserProfiles = useTranslationUserProfiles();
	const FIELD_NAME = useMemo(
		() => ({
			[PIV_FORM.PIV_NAME]: tUserProfiles('piv.company_name'),
			[PIV_FORM.PIV_REG_NUMBER]: tUserProfiles('piv.company_number'),
			[PIV_FORM.PIV_COUNTRY]: tUserProfiles('personal_details.country'),
			[PIV_FORM.PIV_CITY]: tUserProfiles('personal_details.city'),
			[PIV_FORM.PIV_POST_CODE]: tUserProfiles('personal_details.postal_code'),
			[PIV_FORM.PIV_STREET]: tUserProfiles('personal_details.street'),
			[PIV_FORM.PIV_HOUSE_NUMBER]: tUserProfiles('personal_details.house_number'),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME]: tUserProfiles(
				'personal_details.first_name'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME]: tUserProfiles(
				'personal_details.last_name'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL]: tUserProfiles(
				'piv.legal_representative_form.email_address'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE]: tUserProfiles(
				'personal_details.date_of_birth'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_HOUSE_NUMBER]: tUserProfiles(
				'personal_details.house_number'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_STREET]:
				tUserProfiles('personal_details.street'),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_CITY]: tUserProfiles('personal_details.city'),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_COUNTRY]: tUserProfiles(
				'personal_details.country'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_POST_CODE]: tUserProfiles(
				'personal_details.postal_code'
			),
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER]: tUserProfiles(
				'personal_details.phone_number'
			),
		}),
		[]
	);
	const requiredIfInvestorsNotLegalRep = useCallback(
		(validatorWhenTrue = commonYup.string.required) => {
			return Yup.string().when(
				[LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR],
				{
					is: false,
					then: () => validatorWhenTrue,
				}
			);
		},
		[]
	);

	const getUserProfilesRequest = useQuery({
		queryKey: [API_PATHS.USER_PROFILE_GET],
		queryFn: ({ signal }) => getUserProfile(API_PATHS.USER_PROFILE_GET, signal),
	});
	const updateLegalRepresentativeWithoutMipIdRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateLegalRepresentativeWithoutMipId>[0]) =>
			updateLegalRepresentativeWithoutMipId(variables),
	});
	const updateVehicleDataWithoutMipIdRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateVehicleDataWithoutMipId>[0]) =>
			updateVehicleDataWithoutMipId(variables),
	});
	const commonYup = useCommonYupValidation();
	const [isEditModeEnabled, setIsEditModeEnabled] = useState({
		[FORM_TYPE.PIV]: false,
		[FORM_TYPE.LEGAL_REPRESENTATIVE]: false,
	});
	const [showModal, setShowModal] = useState({
		[FORM_TYPE.PIV]: false,
		[FORM_TYPE.LEGAL_REPRESENTATIVE]: false,
	});
	const [changeInvestorAsLegalRepresentative, setChangeInvestorAsLegalRepresentative] =
		useState(false);

	const validationSchema = useMemo(() => {
		return {
			[FORM_TYPE.PIV]: Yup.object().shape({
				[PIV_FORM.PIV_COUNTRY]: commonYup.string.required,
				[PIV_FORM.PIV_CITY]: commonYup.string.required,
				[PIV_FORM.PIV_POST_CODE]: commonYup.string.required,
				[PIV_FORM.PIV_STREET]: commonYup.string.required,
				[PIV_FORM.PIV_HOUSE_NUMBER]: commonYup.string.required,
			}),
			[FORM_TYPE.LEGAL_REPRESENTATIVE]: Yup.object().shape({
				[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME]:
					requiredIfInvestorsNotLegalRep(),
				[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME]:
					requiredIfInvestorsNotLegalRep(),
				[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL]: requiredIfInvestorsNotLegalRep(
					commonYup.email.required
				),
				[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER]:
					requiredIfInvestorsNotLegalRep(commonYup.phone.required),
			}),
		};
	}, []);

	const user = getUserProfilesRequest?.data?.payload;
	const isRepresentativeSameAsUser = user?.isRepresentativeSameAsUser;

	const defaultValues = {
		[FORM_TYPE.PIV]: {
			[PIV_FORM.PIV_NAME]: user?.vehicleData?.pivName ?? '',
			[PIV_FORM.PIV_REG_NUMBER]: user?.vehicleData?.corporateNumber ?? '',
			[PIV_FORM.PIV_COUNTRY]: user?.vehicleData?.address?.country ?? '',
			[PIV_FORM.PIV_CITY]: user?.vehicleData?.address?.city ?? '',
			[PIV_FORM.PIV_POST_CODE]: user?.vehicleData?.address?.postCode ?? '',
			[PIV_FORM.PIV_STREET]: user?.vehicleData?.address?.street ?? '',
			[PIV_FORM.PIV_HOUSE_NUMBER]: user?.vehicleData?.address?.houseNumber ?? '',
		},
		[FORM_TYPE.LEGAL_REPRESENTATIVE]: {
			[LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR]:
				isRepresentativeSameAsUser ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME]:
				user?.representative?.firstName ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME]:
				user?.representative?.lastName ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL]: user?.representative?.email ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE]:
				user?.representative?.birthDate ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_HOUSE_NUMBER]:
				user?.representative?.address?.houseNumber ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_STREET]:
				user?.representative?.address?.street ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_CITY]:
				user?.representative?.address?.city ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_COUNTRY]:
				user?.representative?.address?.country ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_POST_CODE]:
				user?.representative?.address?.postCode ?? '',
			[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER]: user?.representative
				?.phoneNumber
				? user?.representative?.telephonePrefix + user?.representative?.phoneNumber
				: '',
		},
	};
	const PIVForm = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(validationSchema[FORM_TYPE.PIV]),
	});

	const legalRepresentativeForm = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(validationSchema[FORM_TYPE.LEGAL_REPRESENTATIVE]),
	});

	const changeToViewMode = (formType: string) => {
		if (formType === FORM_TYPE.LEGAL_REPRESENTATIVE) {
			legalRepresentativeForm.reset(defaultValues[formType]);
		} else {
			PIVForm.reset(defaultValues[formType]);
		}
		setIsEditModeEnabled((prev) => ({
			...prev,
			[formType]: false,
		}));
		setShowModal((prev) => ({
			...prev,
			[formType]: false,
		}));
	};
	useEffect(() => {
		if (!user) {
			return;
		}
		PIVForm.reset(defaultValues[FORM_TYPE.PIV]);
		legalRepresentativeForm.reset(defaultValues[FORM_TYPE.LEGAL_REPRESENTATIVE]);
	}, [user]);

	const isInvestorLegalRepresentativeByDefault =
		!!defaultValues[FORM_TYPE.LEGAL_REPRESENTATIVE][
			LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
		];

	useEffect(() => {
		const { resetField } = legalRepresentativeForm;
		if (isInvestorLegalRepresentativeByDefault) {
			setIsEditModeEnabled((prev) => ({
				...prev,
				[FORM_TYPE.LEGAL_REPRESENTATIVE]: true,
			}));
			return;
		}
		if (
			!legalRepresentativeForm.watch(
				LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
			)
		) {
			legalRepresentativeForm.reset(defaultValues[FORM_TYPE.LEGAL_REPRESENTATIVE]);
			setChangeInvestorAsLegalRepresentative(false);
			return;
		}

		setIsEditModeEnabled((prev) => ({
			...prev,
			[FORM_TYPE.LEGAL_REPRESENTATIVE]: true,
		}));
		setChangeInvestorAsLegalRepresentative(true);

		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME, {
			defaultValue: user?.firstName,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME, {
			defaultValue: user?.lastName,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE, {
			defaultValue: user?.birthDate,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL, {
			defaultValue: user?.email,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER, {
			defaultValue: `${user?.representative?.telephonePrefix}  ${user?.representative?.phoneNumber}`,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_HOUSE_NUMBER, {
			defaultValue: user?.address?.houseNumber,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_STREET, {
			defaultValue: user?.address?.street,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_CITY, {
			defaultValue: user?.address?.city,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_COUNTRY, {
			defaultValue: user?.address?.country,
		});
		resetField(LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_POST_CODE, {
			defaultValue: user?.address?.postCode,
		});
	}, [
		legalRepresentativeForm.watch(LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR),
	]);

	const onLegalRepresentativeSubmit = useCallback((formData: UpdateUserRepresentativeDto) => {
		const phoneNumber =
			formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER] &&
			normalizePhoneNumberForBE(
				formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER],
				formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_TELEPHONE_PREFIX]
			);
		const updatedData: UpdateUserRepresentativeDto = {
			phoneNumber,
			firstName: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME],
			lastName: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME],
			email: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL],
			country: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_COUNTRY],
			city: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_CITY],
			postCode: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_POST_CODE],
			street: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_STREET],
			houseNumber: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_HOUSE_NUMBER],
			telephonePrefix: formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_TELEPHONE_PREFIX],
		};
		const birthDateFormValue = formData[LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE];
		if (birthDateFormValue) {
			updatedData.birthDate = birthDateFormValue;
		}
		updateLegalRepresentativeWithoutMipIdRequest.mutate(
			{
				...updatedData,
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tUserProfiles('toast.title', {
							section_name: 'legal representative',
						}),
						message: tUserProfiles('toast.description'),
					});
					void getUserProfilesRequest.refetch();
					setIsEditModeEnabled((prev) => ({
						...prev,
						[FORM_TYPE.LEGAL_REPRESENTATIVE]: false,
					}));
					setChangeInvestorAsLegalRepresentative(false);
				},
			}
		);
	}, []);

	const onPIVSubmit = useCallback((formData: UpdateUserVehicleDataDto) => {
		updateVehicleDataWithoutMipIdRequest.mutate(
			{
				country: formData[PIV_FORM.PIV_COUNTRY],
				city: formData[PIV_FORM.PIV_CITY],
				postCode: formData[PIV_FORM.PIV_POST_CODE],
				street: formData[PIV_FORM.PIV_STREET],
				houseNumber: formData[PIV_FORM.PIV_HOUSE_NUMBER],
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tUserProfiles('toast.title', {
							section_name: 'PIV data',
						}),
						message: tUserProfiles('toast.description'),
					});
					void getUserProfilesRequest.refetch();
					setIsEditModeEnabled((prev) => ({
						...prev,
						[FORM_TYPE.PIV]: false,
					}));
				},
			}
		);
	}, []);
	const isPIVFormDirty = useMemo(() => {
		return !!(
			PIVForm.formState.dirtyFields[PIV_FORM.IS_PIV] ||
			PIVForm.formState.dirtyFields[PIV_FORM.PIV_HOUSE_NUMBER] ||
			PIVForm.formState.dirtyFields[PIV_FORM.PIV_STREET] ||
			PIVForm.formState.dirtyFields[PIV_FORM.PIV_CITY] ||
			PIVForm.formState.dirtyFields[PIV_FORM.PIV_POST_CODE] ||
			PIVForm.formState.dirtyFields[PIV_FORM.PIV_COUNTRY]
		);
	}, [defaultValues[FORM_TYPE.PIV], PIVForm.watch(), PIVForm.formState]);

	const isLegalRepFormDirty = useMemo(() => {
		const formDateValue = legalRepresentativeForm.getValues(
			LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE
		);
		const formPhoneValue = legalRepresentativeForm.getValues(
			LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER
		);
		const isDateChanged =
			formDateValue &&
			!isSameDay(
				new Date(defaultValues[FORM_TYPE.LEGAL_REPRESENTATIVE].representativeBirthDate),
				new Date(formDateValue)
			);
		const phoneNumber =
			formPhoneValue && normalizePhoneNumberForFormValueComparison(formPhoneValue);

		const isPhoneNumberChange =
			phoneNumber !== defaultValues[FORM_TYPE.LEGAL_REPRESENTATIVE].representativePhoneNumber;

		const isOtherFieldChanged =
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_HOUSE_NUMBER
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_STREET
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_CITY
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_COUNTRY
			] ||
			legalRepresentativeForm.formState.dirtyFields[
				LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_POST_CODE
			];
		return !!isDateChanged || isPhoneNumberChange || !!isOtherFieldChanged;
	}, [
		defaultValues[FORM_TYPE.LEGAL_REPRESENTATIVE],
		legalRepresentativeForm.watch(),
		legalRepresentativeForm.formState,
	]);

	const renderPlaceHolderText = useCallback(
		(fieldName: string) =>
			tUserProfiles('placeholder_text', {
				field_name: fieldName.toLowerCase(),
			}),
		[]
	);

	return {
		onLegalRepresentativeSubmit,
		onPIVSubmit,
		isEditModeEnabled,
		setIsEditModeEnabled,
		PIVForm,
		legalRepresentativeForm,
		showModal,
		setShowModal,
		isLoading: getUserProfilesRequest?.isLoading,
		changeToViewMode,
		user,
		isPIVFormDirty,
		isLegalRepFormDirty,
		FIELD_NAME,
		renderPlaceHolderText,
		isInvestorLegalRepresentativeByDefault,
		changeInvestorAsLegalRepresentative,
		setChangeInvestorAsLegalRepresentative,
	};
};
